import React, { Suspense, Fragment, lazy } from 'react';
import { Switch, Redirect, Route } from 'react-router-dom';
import DashboardLayout from 'src/layouts/DashboardLayout';
import LoadingScreen from 'src/components/LoadingScreen';
import AuthGuard from 'src/components/AuthGuard';
import GuestGuard from 'src/components/GuestGuard';

type Routes = {
  exact?: boolean;
  path?: string | string[];
  guard?: any;
  layout?: any;
  component?: any;
  routes?: Routes;
}[];

export const renderRoutes = (routes: Routes = []): JSX.Element => (
  <Suspense fallback={<LoadingScreen />}>
    <Switch>
      {routes.map((route, i) => {
        const Guard = route.guard || Fragment;
        const Layout = route.layout || Fragment;
        const Component = route.component;

        return (
          <Route
            key={i}
            path={route.path}
            exact={route.exact}
            render={props => (
              <Guard>
                <Layout>
                  {route.routes ? (
                    renderRoutes(route.routes)
                  ) : (
                    <Component {...props} />
                  )}
                </Layout>
              </Guard>
            )}
          />
        );
      })}
    </Switch>
  </Suspense>
);

const prodAppRoutes = [
  {
    exact: true,
    path: '/app/checkins/captive-portal',
    component: lazy(() =>
      import('src/views/checkins/CheckinsCaptivePortalView')
    )
  },
  {
    exact: true,
    path: '/app/checkins/profiles',
    component: lazy(() => import('src/views/checkins/CheckinsProfilesView'))
  },
  {
    exact: true,
    path: '/app/realtime/overview',
    component: lazy(() => import('src/views/realtime2.0'))
  },
  {
    exact: true,
    path: '/app/sensors/overview',
    component: lazy(() => import('src/views/doorSensors'))
  },
  {
    exact: true,
    path: '/app/snapshots/overview',
    component: lazy(() => import('src/views/snapshots'))
  },
  {
    exact: true,
    path: '/app/dashboard/overview',
    component: lazy(() => import('src/views/dashboard/DashboardOverview'))
  },
  {
    exact: true,
    path: '/app/zoning/overview',
    component: lazy(() => import('src/views/zoning'))
  },
  {
    exact: true,
    path: '/app/poi/overview',
    component: lazy(() => import('src/views/poi'))
  },
  {
    exact: true,
    path: '/app/cameras/overview',
    component: lazy(() => import('src/views/cameras'))
  },
  {
    exact: true,
    path: '/app/queue/overview',
    component: lazy(() => import('src/views/queueManagement'))
  },
  {
    exact: true,
    path: '/app/heatmap/overview',
    component: lazy(() => import('src/views/heatmap'))
  },
  {
    exact: true,
    path: '/app/manage/users',
    component: lazy(() => import('src/views/users/UserManagementView'))
  },
  {
    exact: true,
    path: '/app/manage/users/create',
    component: lazy(() => import('src/views/users/CreateUserView'))
  },
  {
    exact: true,
    path: '/app/manage/users/edit/:userId',
    component: lazy(() => import('src/views/users/EditUserView'))
  },
  {
    exact: true,
    path: '/app/manage/business-hours',
    component: lazy(() => import('src/views/businessHours/BusinessHoursView'))
  },
  {
    exact: true,
    path: '/app/manage/zones',
    component: lazy(() => import('src/views/zones/ZonesOverview'))
  },
  {
    exact: true,
    path: '/app/manage/access-points',
    component: lazy(() => import('src/views/sensors'))
  },
  {
    exact: true,
    path: '/app/manage/locations',
    component: lazy(() => import('src/views/locations'))
  },
  {
    exact: true,
    path: '/app/manage/locations/audit-log',
    component: lazy(() => import('src/views/locations/AuditLog'))
  },
  {
    exact: true,
    path: '/app/manage/cameras',
    component: lazy(() => import('src/views/cameras'))
  },
  {
    exact: true,
    path: '/app/manage/poi',
    component: lazy(() => import('src/views/poiSettings'))
  },
  {
    exact: true,
    path: '/app/manage/cameras/monitoring',
    component: lazy(() => import('src/views/cameraMonitoring'))
  },
  {
    exact: true,
    path: '/app/manage/brands',
    component: lazy(() => import('src/views/brands'))
  },
  {
    exact: true,
    path: '/app/brand/overview',
    component: lazy(() => import('src/views/brandOverview'))
  }
];

export const prodRoutes: Routes = [
  {
    exact: true,
    path: '/404',
    component: lazy(() => import('src/views/errors/NotFoundView'))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/login',
    component: lazy(() => import('src/views/auth/LoginView'))
  },
  {
    exact: true,
    path: '/login-unprotected',
    component: lazy(() => import('src/views/auth/LoginView'))
  },
  {
    exact: true,
    guard: AuthGuard,
    path: '/callback',
    component: () => <Redirect to="/app/dashboard/overview" />
  },
  {
    path: '/app',
    guard: AuthGuard,
    layout: DashboardLayout,
    routes: prodAppRoutes
  },
  {
    path: '*',
    routes: [
      {
        exact: true,
        path: '/',
        component: () => <Redirect to="/app/dashboard/overview" />
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  }
];
